/**
 * Compatibility layer for FHIR R4 and R5 Device resources.
 *
 * The API may return either version and may convey R5 fields on an R4
 * resource through cross-version extensions. The UI uses this adapter rather
 * than binding itself to one generated FHIR TypeScript interface.
 */
export type FhirDevice = Record<string, any>;

export type DeviceKind = "software" | "material";

export const FHIR_DEVICE_CATEGORY_SYSTEM =
  "http://hl7.org/fhir/device-category";

const CATEGORY_CODE_BY_KIND: Record<DeviceKind, string> = {
  material: "material",
  software: "software",
};

export function addRequiredCategory(
  device: FhirDevice,
  kind: DeviceKind,
): FhirDevice {
  const code = CATEGORY_CODE_BY_KIND[kind];
  const display = kind === "software" ? "Software" : "Material";
  const categories = asArray(device.category);
  const hasCategory = categories.some((category: any) =>
    category?.coding?.some(
      (coding: any) =>
        coding.system === FHIR_DEVICE_CATEGORY_SYSTEM && coding.code === code,
    ),
  );

  return {
    ...device,
    resourceType: "Device",
    category: hasCategory
      ? categories
      : [
          ...categories,
          {
            coding: [{ system: FHIR_DEVICE_CATEGORY_SYSTEM, code, display }],
            text: display,
          },
        ],
  };
}

const asArray = <T>(value: T | T[] | undefined | null): T[] =>
  value === undefined || value === null
    ? []
    : Array.isArray(value)
      ? value
      : [value];

const displayCoding = (coding?: any): string =>
  coding?.display ?? coding?.code ?? "";

const isDeviceCategoryCoding = (coding: any, code: string): boolean =>
  coding?.code?.toLowerCase() === code &&
  (!coding.system ||
    coding.system === FHIR_DEVICE_CATEGORY_SYSTEM ||
    coding.system.toLowerCase().includes("device-category"));

export function formatCodeableConcept(value?: any): string {
  if (!value) {
    return "";
  }

  const values = asArray(value.coding).map(displayCoding).filter(Boolean);
  return values.length > 0 ? values.join(", ") : (value.text ?? "");
}

export function getExtensionValue(
  resource: FhirDevice,
  elementName: string,
): any {
  const normalizedElementName = elementName.toLowerCase();
  const extension = asArray(resource.extension).find(
    (item: any) =>
      typeof item?.url === "string" &&
      (item.url.toLowerCase().endsWith(`-${normalizedElementName}`) ||
        item.url.toLowerCase().endsWith(`.${normalizedElementName}`)),
  );

  if (!extension) {
    return undefined;
  }

  return Object.entries(extension).find(([key]) =>
    key.startsWith("value"),
  )?.[1];
}

export function getDeviceName(device: FhirDevice): string {
  if (device.displayName) {
    return device.displayName;
  }

  const r5Name =
    asArray(device.name).find((name: any) => name?.display)?.value ??
    asArray(device.name)[0]?.value;
  if (r5Name) {
    return r5Name;
  }

  const r4Name =
    asArray(device.deviceName).find(
      (name: any) => name?.type === "user-friendly-name",
    )?.name ?? asArray(device.deviceName)[0]?.name;
  return r4Name ?? getExtensionValue(device, "displayName") ?? device.id ?? "";
}

export function getDeviceCategories(device: FhirDevice): any[] {
  return asArray(device.category ?? getExtensionValue(device, "category"));
}

export function getDeviceTypes(device: FhirDevice): any[] {
  return asArray(device.type ?? getExtensionValue(device, "type"));
}

export function getCategoryDisplay(device: FhirDevice): string {
  return getDeviceCategories(device)
    .map(formatCodeableConcept)
    .filter(Boolean)
    .join(", ");
}

export function getTypeDisplay(device: FhirDevice): string {
  return getDeviceTypes(device)
    .map(formatCodeableConcept)
    .filter(Boolean)
    .join(", ");
}

export function getAvailability(device: FhirDevice): string {
  return formatCodeableConcept(
    device.availabilityStatus ??
      getExtensionValue(device, "availabilityStatus"),
  );
}

export function getDeviceVersion(device: FhirDevice): string {
  const versions = asArray(device.version);
  const latest = versions.find((item: any) =>
    asArray(item?.type?.coding).some(
      (coding: any) =>
        coding?.code === "lastRelease" || coding?.code === "software-revision",
    ),
  );

  return latest?.value ?? versions[0]?.value ?? "";
}

function hasStandardCategory(device: FhirDevice, kind: DeviceKind): boolean {
  return getDeviceCategories(device)
    .flatMap((category: any) => asArray(category?.coding))
    .some((coding: any) =>
      isDeviceCategoryCoding(coding, CATEGORY_CODE_BY_KIND[kind]),
    );
}

export function isSoftwareDevice(device: FhirDevice): boolean {
  if (hasStandardCategory(device, "software")) {
    return true;
  }

  const profiles = asArray(device.meta?.profile).join(" ").toLowerCase();
  if (
    profiles.includes("devicesoftware") ||
    profiles.includes("device-software")
  ) {
    return true;
  }

  return getDeviceCategories(device)
    .flatMap((category: any) => asArray(category?.coding))
    .some(
      (coding: any) =>
        typeof coding?.system === "string" &&
        coding.system.includes("COS-DeviceSoftwareCategory"),
    );
}

export function isDeviceKind(device: FhirDevice, kind: DeviceKind): boolean {
  return kind === "software"
    ? isSoftwareDevice(device)
    : hasStandardCategory(device, "material");
}

export function getReferenceDisplay(reference?: any): string {
  if (!reference) {
    return "";
  }

  return (
    reference.display ??
    reference.reference ??
    reference.identifier?.value ??
    ""
  );
}

export function getContactDisplay(contact?: any): string {
  return asArray(contact)
    .map((item: any) =>
      [item.system, item.value, item.use].filter(Boolean).join(": "),
    )
    .filter(Boolean)
    .join(", ");
}

export function getIdentifierDisplay(identifiers?: any): string {
  return asArray(identifiers)
    .map((identifier: any) => {
      if (!identifier.value) {
        return "";
      }

      return identifier.system
        ? `${identifier.value} (${identifier.system})`
        : identifier.value;
    })
    .filter(Boolean)
    .join(", ");
}

export function getDeviceSearchText(device: FhirDevice): string {
  return [
    getDeviceName(device),
    getCategoryDisplay(device),
    getTypeDisplay(device),
    getIdentifierDisplay(device.identifier),
    device.manufacturer,
    device.modelNumber,
    device.serialNumber,
    getReferenceDisplay(device.location),
    getAvailability(device),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
}

export function getDeviceCategoriesForFilter(
  devices: FhirDevice[],
): Array<{ code: string; label: string }> {
  const categories = new Map<string, string>();

  devices.forEach((device) => {
    getDeviceCategories(device).forEach((category: any) => {
      asArray(category?.coding).forEach((coding: any) => {
        if (coding?.code) {
          categories.set(
            `${coding.system ?? ""}|${coding.code}`,
            displayCoding(coding),
          );
        }
      });
    });
  });

  return Array.from(categories, ([code, label]) => ({ code, label })).sort(
    (a, b) => a.label.localeCompare(b.label),
  );
}

export function hasCategory(device: FhirDevice, categoryKey: string): boolean {
  return getDeviceCategories(device).some((category: any) =>
    asArray(category?.coding).some(
      (coding: any) =>
        `${coding.system ?? ""}|${coding.code ?? ""}` === categoryKey,
    ),
  );
}
